import { cl } from '@fullcalendar/core/internal-common';
import {
  IconChevronDown,
  IconDefaultProfile,
  IconExport,
  IconMessage,
  IconWarning,
} from 'assets/icons';
import ClipboardCopy from 'components/ClipboardCopy/ClipboardCopy';
import CheckBox from 'components/common/CheckBox';
import { getEnvConstant } from 'constants/env';
import { SALE_DETAIL_PATH, SALE_LIST_PATH } from 'constants/path';
import { useIsMobile } from 'hooks/common';
import { useEffect, useState } from 'react';

import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import {
  Booking,
  BookingState,
  OrderTransactionActionStatus,
  TicketState,
} from 'types/api';
import { formatPhoneNumber } from 'utils/formatting';
import { WebviewOperatorType } from 'utils/webview';
import { isWebview } from 'utils/webview';
import { callWebviewHandler } from 'utils/webview';

const UserCardContainer = styled.div`
  display: flex;
  flex-direction: column;

  width: 100%;

  padding: 20px 16px 0px;
  border-radius: 12px;

  background-color: #f7f7f8;
`;

const UserBriefInfoBox = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;

  padding-bottom: 20px;

  width: 100%;

  cursor: pointer;

  > img[data-type='thumbnail'] {
    min-width: 52px;
    min-height: 52px;
    max-width: 52px;
    max-height: 52px;

    border-radius: 16px;
  }
`;

const ChevronBox = styled.div<{ $isOpen: boolean }>`
  display: flex;
  align-items: center;
  gap: 4px;

  margin-left: auto;
  cursor: pointer;

  transform: rotate(${({ $isOpen }) => ($isOpen ? '180deg' : '0deg')});
  transition: transform 0.3s ease-in-out;
`;

const UserInfoBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;

  > .basic-info-box {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 4px;

    > p {
      font-weight: 500;
      font-size: 16px;
      line-height: 22px;

      color: rgba(70, 71, 76, 1);

      &[data-type='male'] {
        font-weight: 400;
        font-size: 14px;
        line-height: 20px;

        color: rgba(48, 54, 209, 1);
      }

      &[data-type='female'] {
        font-weight: 400;
        font-size: 14px;
        line-height: 20px;

        color: rgba(162, 109, 231, 1);
      }

      &[data-type='name'] {
        font-weight: 500;
        font-size: 16px;
        line-height: 22px;
      }
    }
  }

  > p[data-type='opt-name'] {
    font-weight: 400;
    font-size: 15px;
    line-height: 22px;

    color: rgba(152, 155, 162, 1);
  }
`;

const UserDetailInfoBox = styled.div`
  display: flex;
  flex-direction: column;

  width: 100%;

  padding-bottom: 20px;
`;

const OverviewBox = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;

  margin: 0px 0 16px;

  p[data-type='total-count'] {
    font-weight: 500;
    font-size: 16px;
    line-height: 22px;

    color: rgba(70, 71, 76, 1);
  }
`;

const UserCardDetailBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;

  margin-bottom: 24px;
  padding-bottom: 24px;
  border-bottom: 1px solid;
  border-color: rgba(234, 235, 236, 1);
`;

const DataRow = styled.div<{ $gap?: string }>`
  display: flex;
  align-items: flex-start;
  gap: ${({ $gap }) => ($gap ? $gap : '12px')};
  flex-wrap: wrap;

  p[data-type='ticket-title'] {
    font-weight: 500;
    font-size: 16px;
    line-height: 22px;

    color: rgba(65, 75, 86, 1);

    &.cancel {
      text-decoration: line-through;
    }
  }

  p[data-type='booking-text'] {
    font-weight: 400;
    font-size: 14px;
    line-height: 20px;

    color: rgba(152, 155, 162, 1);
  }

  p[data-type='bookingId-text'] {
    font-weight: 400;
    font-size: 14px;
    line-height: 20px;

    color: rgba(152, 155, 162, 1);
  }

  p[data-type='query-text'] {
    min-width: 25px;

    font-weight: 500;
    font-size: 14px;
    line-height: 20px;

    color: rgba(70, 71, 76, 1);
  }

  p[data-type='answer-text'] {
    font-weight: 400;
    font-size: 14px;
    line-height: 20px;

    color: rgba(112, 115, 124, 1);
  }
`;

const WhiteBookingItemBoxContainer = styled.div<{
  $isChecked: boolean;
  $direction: 'row' | 'column';
}>`
  display: flex;
  justify-content: space-between;
  gap: 12px;

  flex-direction: ${({ $direction }) =>
    $direction === 'row' ? 'row' : 'column'};

  border-radius: 12px;

  padding: 20px 16px;

  background-color: #fff;

  ${({ $isChecked }) => `
    border:  ${
      $isChecked ? '1.5px solid #000000' : '1px solid rgba(234, 235, 236, 1)'
    };
  `}

  div[data-type='single-query'] {
    display: flex;
    flex-direction: column;
    gap: 8px;

    margin-bottom: 16px;
    padding-bottom: 16px;

    border-bottom: 1px solid;
    border-color: rgba(234, 235, 236, 1);

    &.last {
      padding-bottom: 0px;
      margin-bottom: 0px;
      border-bottom: 0;
    }
  }
`;

const WhiteQueryBoxContainer = styled.div<{
  $isChecked: boolean;
  $direction: 'row' | 'column';
}>`
  display: flex;
  justify-content: space-between;
  gap: 12px;

  flex-direction: ${({ $direction }) =>
    $direction === 'row' ? 'row' : 'column'};

  border: 1px solid;

  border-radius: 12px;

  padding: 16px;

  background-color: #fff;

  ${({ $isChecked }) => `
    border-color:  ${$isChecked ? '#000000' : 'rgba(234, 235, 236, 1)'};
  `}

  div[data-type='single-query'] {
    display: flex;
    flex-direction: column;
    gap: 8px;

    margin-bottom: 16px;
    padding-bottom: 16px;

    border-bottom: 1px solid;
    border-color: rgba(234, 235, 236, 1);

    &.last {
      padding-bottom: 0px;
      margin-bottom: 0px;
      border-bottom: 0;
    }
  }
`;

const ProductInfoBox = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;

  margin-bottom: 24px;

  > img {
    width: 44px;
    height: 44px;

    border-radius: 8px;
  }

  > p {
    color: #70737c;
    font-weight: 500;
    font-size: 14px;
    line-height: 20px;
  }
`;

const ButtonBox = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 16px;

  color: #c2c4c8;

  > div {
    display: flex;
    align-items: center;
    gap: 6px;

    cursor: pointer;

    > p {
      color: #70737c;
      font-weight: 500;
      font-size: 16px;
      line-height: 22px;
    }
  }

  > button {
    height: 48px;

    padding: 0 24px;
    margin-left: 8px;

    background-color: #eaebec;
    border-radius: 12px;

    font-weight: 600;
    font-size: 16px;
    color: #dbdcdf;
    line-height: 48px;

    &.active {
      background-color: #0071e9;
      color: #ffffff;
    }
  }
`;

/* TODO: 컬러값 수정 필요 */
const Badge = styled.div<{
  $status: BookingState | OrderTransactionActionStatus | TicketState;
}>`
  height: 24px;
  min-width: 51px;
  width: fit-content;

  padding: 0px 6px;

  border-radius: 6px;

  font-weight: 600;
  font-size: 11px;
  line-height: 24px;

  ${({ $status }) => `
    background-color: ${
      $status === BookingState.ABSENT
        ? 'rgba(255, 230, 230, 1)'
        : $status === OrderTransactionActionStatus.CANCELLATION_REQUESTED
          ? 'rgba(255, 230, 230, 1)'
          : $status === OrderTransactionActionStatus.CANCELLATION_COMPLETED
            ? 'rgba(255, 230, 230, 1)'
            : $status === BookingState.APPLIED
              ? 'rgba(234, 235, 236, 1)'
              : $status === BookingState.CANCELED
                ? 'rgba(255, 230, 230, 1)'
                : $status === BookingState.CONFIRMED
                  ? 'rgba(213, 233, 255, 1)'
                  : $status === BookingState.USED
                    ? 'rgba(213, 233, 255, 1)'
                    : 'rgba(255, 235, 238, 1)'
    };

    color: ${
      $status === BookingState.ABSENT
        ? 'rgba(255, 66, 66, 1)'
        : $status === OrderTransactionActionStatus.CANCELLATION_REQUESTED
          ? 'rgba(255, 66, 66, 1)'
          : $status === OrderTransactionActionStatus.CANCELLATION_COMPLETED
            ? 'rgba(255, 66, 66, 1)'
            : $status === BookingState.APPLIED
              ? 'rgba(70, 71, 76, 1)'
              : $status === BookingState.CANCELED
                ? 'rgba(255, 66, 66, 1)'
                : $status === BookingState.CONFIRMED
                  ? 'rgba(0, 113, 233, 1)'
                  : $status === BookingState.USED
                    ? 'rgba(0, 113, 233, 1)'
                    : 'rgba(255, 235, 238, 1)'
    };
  `}
`;

const CancelButton = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;

  width: 93px;
  height: 40px;

  border-radius: 12px;

  font-weight: 600;
  font-size: 14px;
  line-height: 40px;
  color: rgba(65, 75, 86, 1);

  background-color: rgba(234, 235, 236, 1);

  background-color: #ffe6e6;
  color: #ff4242;
`;

const ButtonContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;

  margin-top: 24px;

  > button {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 4px;

    width: 93px;
    height: 40px;

    border-radius: 12px;

    font-weight: 600;
    font-size: 14px;
    line-height: 40px;
    color: rgba(65, 75, 86, 1);

    background-color: rgba(234, 235, 236, 1);

    &[data-type='cancel'] {
      background-color: #ffe6e6;
      color: #ff4242;
    }

    svg {
      width: 16px;
      height: 16px;
    }
  }
`;

const CheckAttendanceBox = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;

  margin-left: auto;

  > p {
    color: #989ba2;
    font-weight: 500;
    font-size: 16px;
    line-height: 22px;
  }

  > p[data-type='ABSENT'] {
    color: #ff4242;
  }

  > p[data-type='USED'] {
    color: #0071e9;
  }
`;

const TimelineBox = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 16px;

  border-top: 1px solid;
  border-color: #eaebec;

  padding-top: 24px;

  > div {
    display: flex;
    gap: 16px;

    > p[data-type='timeline-title'] {
      font-weight: 500;
      font-size: 15px;
      line-height: 22px;

      color: #414b56;
    }

    > p[data-type='timeline-date'] {
      font-weight: 400;
      font-size: 15px;
      line-height: 22px;

      color: #70737c;
    }
  }
`;

const CheckboxBox = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  min-width: 47px;

  > p {
    font-weight: 500;
    font-size: 16px;
    line-height: 22px;
    color: #989ba2;

    &.checked {
      color: #414b56;
    }
  }
`;

const CancelRequestBox = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;

  padding: 0px 0px 16px;
  /* margin-top: 16px; */

  p {
    font-weight: 500;
    font-size: 13px;
    line-height: 18px;
    color: #ff4242;
  }
`;

interface UserCardProps {
  TicketData: Booking;
  selectedBookingId: string;
  handleSelectBookingId: (itemId: string) => void;
  selectedTicketIdList: string[];
  handleSelectTicketId: (ticketId: string | string[]) => void;
  handleConfirmDateInCalendarModal: () => void;
  bookingTicketsRefetch: () => void;
}

function UserCard({
  TicketData,
  handleSelectBookingId,
  selectedBookingId,
  selectedTicketIdList,
  handleSelectTicketId,
  handleConfirmDateInCalendarModal,
  bookingTicketsRefetch,
}: UserCardProps) {
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  /* 
    취소요청, 취소완료가 된 순서idx를 모아둔 리스트 

    사용하는 이유는 체크박스 전체 선택시, 취소요청, 취소완료가 된 티켓도 선택 안되게 하기 위함
  */
  const [canceledItemOrderIdxList, setCanceledItemOrderIdxList] = useState<
    Record<string, boolean>
  >({});

  const [isImageError, setIsImageError] = useState(false);

  const isSelected = selectedBookingId === TicketData.id;

  const getBadgeText = (
    bookingState: BookingState | OrderTransactionActionStatus | TicketState,
  ) => {
    switch (bookingState) {
      case OrderTransactionActionStatus.CANCELLATION_REQUESTED:
        return '취소요청';
      case OrderTransactionActionStatus.CANCELLATION_COMPLETED:
        return '취소완료';
      case TicketState.ABSENT:
        return '예약불참';
      case TicketState.APPLIED:
        return '예약대기';
      case TicketState.CANCELED:
        return '취소완료';
      case TicketState.CONFIRMED:
        return '예약확정';
      case TicketState.USED:
        return '예약참석';
      default:
        return '예약대기';
    }
  };

  const transactionItemList = TicketData.order.transactions
    .map(data => {
      if (data.items.length > 0) {
        return data.items
          .map(item => ({
            [item.id]: data.actionStatus,
          }))
          .flat();
      }
    })
    .flat();

  const orderTrxData = transactionItemList.reduce((acc, item) => {
    const key = Object.keys(item)[0];
    const value = item[key];
    return { ...acc, [key]: value };
  }, {});

  const checkableTicketList = TicketData.items.map(item => {
    /* 취소요청, 예약대기 티켓만 존재 */
    const checkableTicketList = item.tickets.filter(
      ticket => ticket.status === TicketState.APPLIED,
    );

    return {
      ...item,
      tickets: checkableTicketList,
    };
  });

  /* 전체 선택했을때, id 리스트 */
  const allTicketIds =
    checkableTicketList
      .map(item => item.tickets[0]?.id)
      .filter((ticket, idx) => {
        if (!canceledItemOrderIdxList[idx]) {
          return ticket;
        }
      })
      .filter(data => data) || [];

  // const allTicketIds =
  //   checkableTicketList
  //     .map((bookingItem, bookingItemIdx) => {
  //       return bookingItem.tickets[0]?.id;
  //     })
  //     .filter((ticket, idx) => {
  //       if (!canceledItemOrderIdxList.includes(idx)) {
  //         return ticket;
  //       }
  //     })
  //     .filter(data => data) || [];

  const canceledCountList = Object.values(orderTrxData).filter(status => {
    if (status === OrderTransactionActionStatus.CANCELLATION_COMPLETED) {
      return status;
    }
  });

  const allBookingOrderData = TicketData.order.items.find(
    item => item.id === TicketData.id,
  );

  const dependedItemList = allBookingOrderData.dependedItems.map(
    dependedItem => {
      return dependedItem.id;
    },
  );
  const allCanceledItem = dependedItemList.some(
    itemId =>
      orderTrxData[itemId] ===
      OrderTransactionActionStatus.CANCELLATION_COMPLETED,
  );

  const isAllTicketSelected =
    selectedTicketIdList.length > 0 &&
    selectedTicketIdList.length === allTicketIds.length;

  const goToSalesPage = (orderId: string) => {
    if (isMobile) {
      navigate(`${SALE_DETAIL_PATH}/${orderId}`);
    } else {
      localStorage.setItem('orderId', orderId);
      navigate(SALE_LIST_PATH);
    }
  };

  const goToMessagePage = (orderId: string) => {
    if (isWebview()) {
      // item.bookingItem.booking.order.id
      callWebviewHandler(WebviewOperatorType.OpenActionUrl, {
        actionUrl: `frip://chattingDetail/O${orderId}`,
      });

      return;
    }

    window.open(`${getEnvConstant('webClientUrl')}/messageDetail/O${orderId}`);
  };

  // useEffect(() => {
  //   if (isSelected) {
  //     console.log('일반 조율형');
  //     console.log('selected TicketData', TicketData);
  //     console.log('selectedTicketIdList', selectedTicketIdList);

  //     console.log('checkableTicketList  ', checkableTicketList);
  //     console.log('allTicketIds  ', allTicketIds);

  //     // console.log('isOnlyCancelRequestedTicket', isOnlyCancelRequestedTicket);
  //   }
  // }, [isSelected]);

  return (
    <UserCardContainer>
      <UserBriefInfoBox onClick={() => handleSelectBookingId(TicketData.id)}>
        {TicketData.customer?.profileImage?.uri && !isImageError ? (
          <img
            data-type="thumbnail"
            alt="thumbnail"
            src={TicketData.customer.profileImage.uri}
            onError={() => setIsImageError(true)}
          />
        ) : (
          <IconDefaultProfile width={52} height={52} />
        )}

        <UserInfoBox>
          <div className="basic-info-box">
            {TicketData.customer?.gender && (
              <>
                <p
                  data-type={
                    TicketData.customer?.gender === 'F' ? 'female' : 'male'
                  }
                >
                  {TicketData.customer?.gender === 'F' ? '여자' : '남자'}
                </p>

                {`\u2022`}
              </>
            )}

            <p data-type="name">{TicketData.customer?.nickname}</p>

            {TicketData.customer?.mobileNumber && (
              <>
                {`\u2022`}
                <p data-type="phone">
                  {formatPhoneNumber(TicketData.customer?.mobileNumber)}
                </p>
              </>
            )}

            <ClipboardCopy value={TicketData.customer.mobileNumber} />
          </div>

          {!isSelected && (
            <p data-type="opt-name">
              {TicketData.info.name || '-'}

              {TicketData.items.length - canceledCountList.length > 1
                ? ` 외 ${
                    TicketData.items.length - canceledCountList.length - 1
                  }개 (총 ${
                    TicketData.items.length - canceledCountList.length
                  }건)`
                : ''}
            </p>
          )}
        </UserInfoBox>

        <ChevronBox $isOpen={isSelected}>
          <IconChevronDown
            width={24}
            height={24}
            color={'rgba(112, 115, 124, 1)'}
          />
        </ChevronBox>
      </UserBriefInfoBox>

      {allBookingOrderData.dependedItems.length > 0 && (
        <CancelRequestBox>
          <IconWarning />
          <p>
            {allCanceledItem
              ? '취소된 예약건이 있어요.'
              : '주문 취소가 요청된 예약건이 있어요.'}
          </p>
        </CancelRequestBox>
      )}

      {isSelected && (
        <UserDetailInfoBox>
          {TicketData.items.length > 1 && (
            <OverviewBox>
              <p data-type="total-count">
                총 {TicketData.items.length - canceledCountList.length}건
              </p>

              {
                // allTicketIds.length > 1 && (
                TicketData.items.length > 1 && (
                  <CheckBox
                    isChecked={isAllTicketSelected}
                    style={{
                      borderRadius: '3px',
                      width: '15px',
                      height: '15px',
                      margin: 0,
                      fontSize: '12px',
                      lineHeight: '15px',
                    }}
                    onChange={() => {
                      handleSelectTicketId(allTicketIds);
                    }}
                    label="전체선택"
                    labelStyle={{
                      color: '#414b56',
                    }}
                  />
                )
              }
            </OverviewBox>
          )}

          {TicketData.items.map(
            ({ tickets, replies, booking, counts }, itemIdx) => {
              const isMultipleTicket = tickets.length > 1;

              const ticketId =
                tickets.find(ticket => ticket.status === TicketState.APPLIED)
                  ?.id || tickets[tickets.length - 1].id;

              const ticketStatus =
                tickets.find(ticket => ticket.status === TicketState.APPLIED)
                  ?.status || tickets[tickets.length - 1].status;

              const bookingOrderData = TicketData.order.items.find(
                data => TicketData.id === data.id,
              );

              const dependedItemList = bookingOrderData.dependedItems.find(
                dependedItem => {
                  return (
                    dependedItem.units.filter(unit => {
                      if (!canceledItemOrderIdxList[unit.indexNo]) {
                        setCanceledItemOrderIdxList(prev => ({
                          ...prev,
                          [unit.indexNo]: true,
                        }));
                      }
                      // if (!canceledItemOrderIdxList.includes(unit.indexNo)) {
                      //   canceledItemOrderIdxList.push(unit.indexNo);
                      // }

                      return unit.indexNo === itemIdx;
                    }).length > 0
                  );
                },
              );

              const canceledItem = dependedItemList;

              const bookingStatus = canceledItem
                ? orderTrxData[canceledItem.id]
                : ticketStatus;

              const isCheckedTicket = selectedTicketIdList.includes(ticketId);
              const isAbleToConfirmed = bookingStatus === TicketState.APPLIED;

              return (
                <UserCardDetailBox key={`booking-user-detail-${itemIdx}`}>
                  <WhiteBookingItemBoxContainer
                    $isChecked={isCheckedTicket}
                    $direction={'row'}
                  >
                    <DataRow>
                      {isMultipleTicket ? (
                        <Badge $status={bookingStatus}>
                          {canceledItem
                            ? '취소완료'
                            : `다회권 총 ${counts.total}개 중 ${counts.applied}개 남음`}
                        </Badge>
                      ) : (
                        <Badge $status={bookingStatus}>
                          {getBadgeText(bookingStatus)}
                        </Badge>
                      )}

                      <p
                        data-type="ticket-title"
                        className={canceledItem ? 'cancel' : ''}
                      >
                        {tickets[0].bookingItem?.booking?.info?.name}
                      </p>
                    </DataRow>

                    {isAbleToConfirmed && TicketData.items.length > 1 && (
                      <CheckBox
                        isChecked={isCheckedTicket}
                        style={{
                          borderRadius: '3px',
                          width: '15px',
                          height: '15px',
                          margin: 0,
                          fontSize: '12px',
                          lineHeight: '15px',
                        }}
                        onChange={() => {
                          handleSelectTicketId(ticketId);
                        }}
                        label="선택"
                        labelStyle={{
                          color: '#414b56',
                        }}
                      />
                    )}
                  </WhiteBookingItemBoxContainer>

                  {replies.length > 0 && (
                    <WhiteQueryBoxContainer
                      $isChecked={isCheckedTicket}
                      $direction={'column'}
                    >
                      {replies.map(({ title, answer, selections }, idx) => {
                        const getAnswerText = () => {
                          if (answer) {
                            return answer;
                          }

                          if (selections) {
                            return selections
                              .map(data => data.label)
                              .join(', ');
                          }

                          return '-';
                        };

                        return (
                          <div
                            key={`booking-user-detail-query-${idx}`}
                            data-type="single-query"
                            className={replies.length - 1 === idx ? 'last' : ''}
                          >
                            <DataRow>
                              <p data-type="query-text">질문</p>
                              <p data-type="answer-text">{title}</p>
                            </DataRow>
                            <DataRow>
                              <p data-type="query-text">답변</p>
                              <p data-type="answer-text">
                                {getAnswerText() || '-'}
                              </p>
                            </DataRow>
                          </div>
                        );
                      })}
                    </WhiteQueryBoxContainer>
                  )}
                </UserCardDetailBox>
              );
            },
          )}

          <ProductInfoBox>
            <img
              data-type="thumbnail"
              alt="thumbnail"
              src={TicketData.info.product.content.thumbnail}
            />
            <p>{TicketData.info.product.title}</p>
          </ProductInfoBox>

          <ButtonBox>
            <div onClick={() => goToSalesPage(TicketData.order.id)}>
              <IconExport width={16} height={16} color={'#70737c'} />
              <p>판매내역</p>
            </div>
            {`\u2502`}
            <div onClick={() => goToMessagePage(TicketData.order.id)}>
              <IconMessage width={16} height={16} color={'#70737c'} />
              <p>메시지</p>
            </div>

            {allTicketIds.length > 0 && (
              <button
                onClick={() => {
                  if (allTicketIds.length === 1) {
                    if (!selectedTicketIdList.includes(allTicketIds[0])) {
                      handleSelectTicketId(allTicketIds[0]);
                    }
                    handleConfirmDateInCalendarModal();
                    return;
                  }

                  if (selectedTicketIdList.length > 0) {
                    handleConfirmDateInCalendarModal();
                  }
                }}
                className={`${
                  allTicketIds.length === 1
                    ? 'active'
                    : isSelected && selectedTicketIdList.length > 0
                      ? 'active'
                      : ''
                }`}
              >
                예약확정
              </button>
            )}
          </ButtonBox>
        </UserDetailInfoBox>
      )}
    </UserCardContainer>
  );
}

export default UserCard;
